import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Chip,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { questionnaireService } from '../services/questionnaireService';
import { QuestionnaireType } from '../types/questionnaire.types';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EventNote as MeetingTypeIcon,
} from '@mui/icons-material';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  meetingTypeService,
  MeetingType,
  MeetingTypeCreate,
  UserType,
  MeetingTypeCategory,
  MEETING_TYPE_CATEGORY_LABELS,
  USER_TYPE_LABELS,
} from '../services/meetingTypeService';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import RequirePermission from '../components/common/RequirePermission';
import { useCrudHandlers } from '../hooks/useCrudHandlers';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { meetingTypeColumns } from '../config/meetingTypeColumns';

const MeetingTypesPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMeetingType, setEditingMeetingType] = useState<MeetingType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [meetingTypeToDelete, setMeetingTypeToDelete] = useState<MeetingType | null>(null);
  const [questionnaireTypes, setQuestionnaireTypes] = useState<QuestionnaireType[]>([]);
  const [selectedQuestionnaireTypeIds, setSelectedQuestionnaireTypeIds] = useState<number[]>([]);
  const [onlyOnce, setOnlyOnce] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset, setValue, control } = useForm<MeetingTypeCreate>();

  const getSortValue = useCallback((meetingType: MeetingType, key: string): any => {
    switch (key) {
      case 'name': return meetingType.name.toLowerCase();
      case 'userType': return meetingType.user_type.toLowerCase();
      case 'type': return meetingType.type || '';
      case 'yomanId': return meetingType.yoman_id;
      case 'interval': return meetingType.yoman_interval;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((meetingType: MeetingType): string[] => {
    return [meetingType.name, meetingType.user_type, meetingType.type || '', meetingType.yoman_id, String(meetingType.yoman_interval)];
  }, []);

  const table = useTablePage<MeetingType>({
    entityType: 'meetingTypes',
    columns: meetingTypeColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  const loadMeetingTypes = useCallback(async () => {
    if (!currentTenant) return;
    table.loadData(() => meetingTypeService.getMeetingTypes(currentTenant.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant]);

  useEffect(() => {
    if (currentTenant) {
      loadMeetingTypes();
      questionnaireService.getQuestionnaireTypes(currentTenant.id)
        .then(setQuestionnaireTypes)
        .catch(() => {});
    }
  }, [currentTenant, loadMeetingTypes]);

  const handleOpenDialog = (meetingType?: MeetingType) => {
    if (meetingType) {
      setEditingMeetingType(meetingType);
      setValue('name', meetingType.name);
      setValue('user_type', meetingType.user_type);
      setValue('type', meetingType.type);
      setValue('yoman_id', meetingType.yoman_id);
      setValue('yoman_interval', meetingType.yoman_interval);
      setSelectedQuestionnaireTypeIds(meetingType.questionnaire_type_ids || []);
      setOnlyOnce(meetingType.only_once ?? false);
    } else {
      setEditingMeetingType(null);
      reset();
      setSelectedQuestionnaireTypeIds([]);
      setOnlyOnce(false);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMeetingType(null);
    reset();
    setSelectedQuestionnaireTypeIds([]);
    setOnlyOnce(false);
  };

  const crud = useCrudHandlers<MeetingType>({
    table,
    createFn: (data) => meetingTypeService.createMeetingType({ ...data, tenant_id: currentTenant!.id }),
    updateFn: (id, data) => meetingTypeService.updateMeetingType(id as number, data),
    deleteFn: (id) => meetingTypeService.deleteMeetingType(id as number),
    entityName: 'Meeting type',
    onSuccess: () => { handleCloseDialog(); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); setMeetingTypeToDelete(null); },
    reloadData: loadMeetingTypes,
  });

  const onSubmit = (data: MeetingTypeCreate) => {
    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }
    crud.handleCreateOrUpdate(
      { ...data, questionnaire_type_ids: selectedQuestionnaireTypeIds.length > 0 ? selectedQuestionnaireTypeIds : null, only_once: onlyOnce },
      editingMeetingType,
    );
  };

  const handleDelete = () => {
    crud.handleDelete(meetingTypeToDelete);
  };

  const getUserTypeLabel = (userType: UserType) => USER_TYPE_LABELS[userType] || userType;
  const getUserTypeColor = (userType: UserType) => userType === UserType.DOCTOR ? 'primary' : 'secondary';

  const getColumnValue = (meetingType: MeetingType, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Box display="flex" alignItems="center">
            <MeetingTypeIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight="medium">{meetingType.name}</Typography>
          </Box>
        );
      case 'userType':
        return <Chip label={getUserTypeLabel(meetingType.user_type)} color={getUserTypeColor(meetingType.user_type)} size="small" />;
      case 'type':
        return meetingType.type ? (
          <Chip label={MEETING_TYPE_CATEGORY_LABELS[meetingType.type]} color="primary" variant="outlined" size="small" />
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        );
      case 'yomanId':
        return <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{meetingType.yoman_id}</Typography>;
      case 'interval':
        return meetingType.yoman_interval;
      default: return '';
    }
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: () => { handleOpenDialog(table.selectedItem!); table.handleMenuClose(); } },
      { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: () => { setMeetingTypeToDelete(table.selectedItem!); setDeleteDialogOpen(true); table.handleMenuClose(); }, color: 'error' as const },
    ];
  };

  const renderMobileCard = (meetingType: MeetingType) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ pr: 5 }}>
        <Box display="flex" alignItems="center">
          <MeetingTypeIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>{meetingType.name}</Typography>
        </Box>
        <Chip label={getUserTypeLabel(meetingType.user_type)} color={getUserTypeColor(meetingType.user_type)} size="small" />
      </Box>
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mb={1}>
        {meetingType.type && (
          <Chip label={MEETING_TYPE_CATEGORY_LABELS[meetingType.type]} color="primary" variant="outlined" size="small" />
        )}
      </Box>
      <Box display="flex" gap={2} alignItems="center">
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>Yoman: {meetingType.yoman_id}</Typography>
        <Typography variant="caption" color="text.secondary">Duration: {meetingType.yoman_interval} min</Typography>
      </Box>
    </>
  );

  return (
    <RequirePermission check={!!user?.user_privilege?.config} featureName="meeting types">
      <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading meeting types:">
        <DataTableToolbar
          {...table.toolbarProps}
          title="Meeting Types"
          searchPlaceholder="Search meeting types by name, user type, or Yoman ID..."
          actions={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={{ whiteSpace: 'nowrap' }}>
              Add Meeting Type
            </Button>
          }
        />

        {isMobile ? (
          <MobileCardGrid
            data={table.sortedData}
            renderCard={renderMobileCard}
            getItemKey={(mt) => mt.id}
            onMenuClick={table.handleRowClick}
            emptyIcon={<MeetingTypeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
            emptyMessage="No meeting types found"
          />
        ) : (
          <DataTableContainer {...table.containerProps}>
            <DataTableBody
              data={table.sortedData}
              {...table.tableBodyProps}
              getColumnValue={getColumnValue}
              getRowKey={(mt) => mt.id}
              onRowClick={table.handleRowClick}
            />
          </DataTableContainer>
        )}

        <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

        <ClinicDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          title={editingMeetingType ? 'Edit Meeting Type' : 'Create Meeting Type'}
          maxWidth="sm"
          onSubmit={handleSubmit(onSubmit)}
          actions={
            <>
              <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
                {editingMeetingType ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleCloseDialog} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
            </>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
              <TextField
                {...register('name', { required: 'Name is required' })}
                fullWidth size="small"
                error={!!errors.name} helperText={errors.name?.message}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>User Type</Typography>
              <Controller
                name="user_type"
                control={control}
                defaultValue={UserType.DOCTOR}
                rules={{ required: 'User type is required' }}
                render={({ field }) => (
                  <Select {...field} fullWidth sx={CLINIC_DIALOG_SX.select}>
                    <MenuItem value={UserType.DOCTOR}>Doctor</MenuItem>
                    <MenuItem value={UserType.CASE_MANAGER}>Case Manager</MenuItem>
                    <MenuItem value={UserType.NURSE_PRESCRIBER}>Nurse Prescriber</MenuItem>
                  </Select>
                )}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Category</Typography>
              <Controller
                name="type"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <Select {...field} fullWidth sx={CLINIC_DIALOG_SX.select}>
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value={MeetingTypeCategory.CASE_MANAGER_INTAKE}>{MEETING_TYPE_CATEGORY_LABELS[MeetingTypeCategory.CASE_MANAGER_INTAKE]}</MenuItem>
                    <MenuItem value={MeetingTypeCategory.PSY_INTAKE}>{MEETING_TYPE_CATEGORY_LABELS[MeetingTypeCategory.PSY_INTAKE]}</MenuItem>
                    <MenuItem value={MeetingTypeCategory.CASE_MANAGER_FOLLOWUP}>{MEETING_TYPE_CATEGORY_LABELS[MeetingTypeCategory.CASE_MANAGER_FOLLOWUP]}</MenuItem>
                    <MenuItem value={MeetingTypeCategory.PSY_FOLLOWUP}>{MEETING_TYPE_CATEGORY_LABELS[MeetingTypeCategory.PSY_FOLLOWUP]}</MenuItem>
                    <MenuItem value={MeetingTypeCategory.TITRATION_INTAKE}>{MEETING_TYPE_CATEGORY_LABELS[MeetingTypeCategory.TITRATION_INTAKE]}</MenuItem>
                    <MenuItem value={MeetingTypeCategory.TITRATION_FOLLOWUP}>{MEETING_TYPE_CATEGORY_LABELS[MeetingTypeCategory.TITRATION_FOLLOWUP]}</MenuItem>
                  </Select>
                )}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Yoman ID</Typography>
              <TextField
                {...register('yoman_id', { required: 'Yoman ID is required' })}
                fullWidth size="small"
                error={!!errors.yoman_id} helperText={errors.yoman_id?.message}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Duration (min)</Typography>
              <TextField
                {...register('yoman_interval', {
                  required: 'Duration is required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Duration must be at least 1 minute' },
                })}
                type="number" fullWidth size="small"
                error={!!errors.yoman_interval} helperText={errors.yoman_interval?.message}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Questionnaires</Typography>
              <Select
                multiple
                fullWidth
                size="small"
                value={selectedQuestionnaireTypeIds}
                onChange={(e) => setSelectedQuestionnaireTypeIds(e.target.value as number[])}
                input={<OutlinedInput />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as number[]).map((id) => {
                      const qt = questionnaireTypes.find((t) => t.id === id);
                      return qt ? <Chip key={id} label={qt.name} size="small" /> : null;
                    })}
                  </Box>
                )}
                sx={CLINIC_DIALOG_SX.select}
              >
                {questionnaireTypes.map((qt) => (
                  <MenuItem key={qt.id} value={qt.id}>
                    <Checkbox checked={selectedQuestionnaireTypeIds.includes(qt.id)} />
                    <ListItemText primary={qt.name} />
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Only Once</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={onlyOnce}
                    onChange={(e) => setOnlyOnce(e.target.checked)}
                    size="small"
                  />
                }
                label="Patients should have at most one meeting of this type"
              />
            </Box>
          </Box>
        </ClinicDialog>

        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDelete}
          entityType="Meeting Type"
          entityName={meetingTypeToDelete?.name}
        />

        <ManageColumnsModal {...table.manageColumnsProps} />
        <SaveViewModal {...table.saveViewModalProps} />
      </TablePageLayout>
    </RequirePermission>
  );
};

export default MeetingTypesPage;
